/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import individualDiagnose from '../../../../share/util/individualDiagnose';
import util from '../../../../share/util/utils';
import { apiErrorDefaultAction } from '../../../../share/util/api/errorAction';
import { PHASE, PHASE_INFO } from './constants';
import { executeApi, handleResponseResult, handleResponseFailed } from './Requirement142Api';

export default {
  name: 'requirement142',
  data() {
    return {
      modal_requirement_142: false,
      phase: PHASE.WAIT,
      isLoading: false,
      systemId: '',
      isDispose: false,
      executeCount: 0,
      bleedingCount: 0,
      countdown: 120,
      timer: null,
    };
  },

  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true },
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId
    }),
    // ダイアログのヘッダー色
    headerColor() { return PHASE_INFO[this.phase]['headerColor']; },
    // ダイアログヘッダ―文言
    headerTitle() { return this.$t(PHASE_INFO[this.phase]['headerTitle']); },

    workSupportMessage() {
      const messageMap = {
        [PHASE.WAIT]: `${this.i18nWsKey}.REQ_WS_142_TXT_1`,
        [PHASE.START]: `${this.i18nWsKey}.REQ_WS_142_TXT_2`,
        [PHASE.RIGHT_FRONT_CAUTION]: `${this.i18nWsKey}.REQ_WS_142_TXT_3`,
        [PHASE.RIGHT_FRONT_NO_NC_VALVE]: `${this.i18nWsKey}.REQ_WS_142_TXT_4`,
        [PHASE.RIGHT_FRONT_BLEEDER_VALVE]: `${this.i18nWsKey}.REQ_WS_142_TXT_5`,
        [PHASE.RIGHT_FRONT_BLEEDER_VALVE_CONFIRMED]: `${this.i18nWsKey}.REQ_WS_142_TXT_6`,
        [PHASE.RIGHT_FRONT_EXECUTE_BLEEDING]: `${this.i18nWsKey}.REQ_WS_142_TXT_7`,
        [PHASE.LEFT_FRONT_CAUTION]: `${this.i18nWsKey}.REQ_WS_142_TXT_8`,
        [PHASE.LEFT_FRONT_NO_NC_VALVE]: `${this.i18nWsKey}.REQ_WS_142_TXT_9`,
        [PHASE.LEFT_FRONT_BLEEDER_VALVE]: `${this.i18nWsKey}.REQ_WS_142_TXT_10`,
        [PHASE.LEFT_FRONT_BLEEDER_VALVE_CONFIRMED]: `${this.i18nWsKey}.REQ_WS_142_TXT_11`,
        [PHASE.LEFT_FRONT_EXECUTE_BLEEDING]: `${this.i18nWsKey}.REQ_WS_142_TXT_12`,
        [PHASE.RIGHT_REAR_START]: `${this.i18nWsKey}.REQ_WS_142_TXT_13`,
        [PHASE.RIGHT_REAR_PREPARATION_1]: `${this.i18nWsKey}.REQ_WS_142_TXT_14`,
        [PHASE.RIGHT_REAR_EXECUTE_BLEEDING_1]: `${this.i18nWsKey}.REQ_WS_142_TXT_15`,
        [PHASE.RIGHT_REAR_PREPARATION_2]: `${this.i18nWsKey}.REQ_WS_142_TXT_16`,
        [PHASE.RIGHT_REAR_EXECUTE_BLEEDING_2]: `${this.i18nWsKey}.REQ_WS_142_TXT_17`,
        [PHASE.LEFT_REAR_PREPARATION_1]: `${this.i18nWsKey}.REQ_WS_142_TXT_18`,
        [PHASE.LEFT_REAR_EXECUTE_BLEEDING_1]: `${this.i18nWsKey}.REQ_WS_142_TXT_19`,
        [PHASE.LEFT_REAR_PREPARATION_2]: `${this.i18nWsKey}.REQ_WS_142_TXT_20`,
        [PHASE.LEFT_REAR_EXECUTE_BLEEDING_2]: `${this.i18nWsKey}.REQ_WS_142_TXT_21`,
        [PHASE.EXECUTE_CONFIRMATION]: `${this.i18nWsKey}.REQ_WS_142_TXT_22`,
        [PHASE.AIR_BLEEDING_SEQ_2]: `${this.i18nWsKey}.REQ_WS_142_TXT_23`,
        [PHASE.AIR_BLEEDING_SEQ_3]: `${this.i18nWsKey}.REQ_WS_142_TXT_24`,
        [PHASE.START_CONFIRMATION]: `${this.i18nWsKey}.REQ_WS_142_TXT_25`,
        [PHASE.AIR_BLEEDING_7]: `${this.i18nWsKey}.REQ_WS_142_TXT_26`,
        [PHASE.AIR_BLEEDING_COMPLETED]: `${this.i18nWsKey}.REQ_WS_142_TXT_27`,
        [PHASE.ERROR]: `${this.i18nWsKey}.REQ_WS_142_TXT_28`,
        [PHASE.FAILED]: 'individual.error_communication_error',
      };
      if (this.phase === PHASE.RIGHT_REAR_EXECUTE_BLEEDING_2
        || this.phase === PHASE.LEFT_REAR_EXECUTE_BLEEDING_2) {
        return this.$t(messageMap[this.phase]).replace('$TIME$', this.countdown);
      }
      if (this.phase === PHASE.AIR_BLEEDING_SEQ_2
        || this.phase === PHASE.AIR_BLEEDING_SEQ_3) {
        return this.$t(messageMap[this.phase]).replaceAll('$TIMES$', this.bleedingCount);
      }
      return this.$t(messageMap[this.phase]);
    },

    showButtonOk() {
      const showOkButtonMap = {
        [PHASE.WAIT]: true,
        [PHASE.START]: true,
        [PHASE.RIGHT_FRONT_CAUTION]: true,
        [PHASE.RIGHT_FRONT_BLEEDER_VALVE]: true,
        [PHASE.RIGHT_FRONT_BLEEDER_VALVE_CONFIRMED]: true,
        [PHASE.RIGHT_FRONT_EXECUTE_BLEEDING]: true,
        [PHASE.LEFT_FRONT_CAUTION]: true,
        [PHASE.LEFT_FRONT_BLEEDER_VALVE]: true,
        [PHASE.LEFT_FRONT_BLEEDER_VALVE_CONFIRMED]: true,
        [PHASE.LEFT_FRONT_EXECUTE_BLEEDING]: true,
        [PHASE.RIGHT_REAR_START]: true,
        [PHASE.RIGHT_REAR_EXECUTE_BLEEDING_1]: true,
        [PHASE.RIGHT_REAR_PREPARATION_2]: true,
        [PHASE.LEFT_REAR_EXECUTE_BLEEDING_1]: true,
        [PHASE.LEFT_REAR_PREPARATION_2]: true,
        [PHASE.EXECUTE_CONFIRMATION]: true,
        [PHASE.START_CONFIRMATION]: true,
        [PHASE.AIR_BLEEDING_COMPLETED]: true,
        [PHASE.ERROR]: true,
        [PHASE.FAILED]: true,
      };
      let result = showOkButtonMap[this.phase];
      if (!result) {
        // 合致しない場合は false とする。
        result = false;
      }
      return result;
    },

    showButtonCancel() {
      const showCancelButtonMap = {
        [PHASE.WAIT]: true,
        [PHASE.START]: true,
      };
      let result = showCancelButtonMap[this.phase];
      if (!result) {
        // 合致しない場合は false とする。
        result = false;
      }
      return result;
    },

    showButtonNoYes() {
      const showNoYesButtonMap = {
        [PHASE.RIGHT_REAR_EXECUTE_BLEEDING_2]: true,
        [PHASE.LEFT_REAR_EXECUTE_BLEEDING_2]: true
      };
      let result = showNoYesButtonMap[this.phase];
      if (!result) {
        // 合致しない場合は false とする。
        result = false;
      }
      return result;
    },
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    /** 初期化処理 */
    init() {
      this.modal_requirement_142 = true;
      // EUCキーの取得
      this.systemId = individualDiagnose.getCurrentSystemId(this);
    },

    /** Wizardを閉じる */
    closeWizard() {
      this.$emit('close-wizard');
      this.modal_requirement_142 = false;
    },

    /** OKボタンクリック時の処理 */
    async onOkClicked() {
      const handlers = {
        [PHASE.WAIT]: () => {
          this.phase = PHASE.START;
          // handlerの型を () => Promise<void> に統一
          return Promise.resolve();
        },
        [PHASE.START]: async () => {
          await this.writeInitialFlag(); // 完了フラグ初期化へ遷移
        },
        [PHASE.RIGHT_FRONT_CAUTION]: async () => {
          this.phase = PHASE.RIGHT_FRONT_NO_NC_VALVE;
          await this.executeDrivingNoNc(); // 右前輪 エアー抜き(N/O・N/C弁強制駆動)へ遷移
        },
        [PHASE.RIGHT_FRONT_BLEEDER_VALVE]: () => {
          this.phase = PHASE.RIGHT_FRONT_BLEEDER_VALVE_CONFIRMED; // 右前輪 エアー抜き(ブリーダーバルブ開閉操作指示)の次の画面へ遷移
          return Promise.resolve();
        },
        [PHASE.RIGHT_FRONT_BLEEDER_VALVE_CONFIRMED]: async () => {
          await this.executeBleeding(); // 右前輪 エアー抜き(エアー抜き実施)へ遷移
        },
        [PHASE.RIGHT_FRONT_EXECUTE_BLEEDING]: async () => {
          await this.stopBleeding(); // 右前輪 エアー抜き(エアー抜き停止)へ遷移
        },
        [PHASE.LEFT_FRONT_CAUTION]: async () => {
          this.phase = PHASE.LEFT_FRONT_NO_NC_VALVE;
          await this.executeDrivingNoNc(); // 左前輪 エアー抜き(N/O・N/C弁強制駆動)へ遷移
        },
        [PHASE.LEFT_FRONT_BLEEDER_VALVE]: () => {
          this.phase = PHASE.LEFT_FRONT_BLEEDER_VALVE_CONFIRMED; // 左前輪 エアー抜き(ブリーダーバルブ開閉操作指示)の次の画面へ遷移
          return Promise.resolve();
        },
        [PHASE.LEFT_FRONT_BLEEDER_VALVE_CONFIRMED]: async () => {
          await this.executeBleeding(); // 左前輪 エアー抜き(エアー抜き実施)へ遷移
        },
        [PHASE.LEFT_FRONT_EXECUTE_BLEEDING]: async () => {
          await this.stopBleeding(); // 左前輪 エアー抜き(エアー抜き停止)へ遷移
        },
        [PHASE.RIGHT_REAR_START]: async () => {
          this.phase = PHASE.RIGHT_REAR_PREPARATION_1;
          await this.executePreparation(); // 右後輪 準備の処理
        },
        [PHASE.RIGHT_REAR_EXECUTE_BLEEDING_1]: async () => {
          await this.stopRearBleeding(); // 右後輪 停止(1回目)へ遷移
        },
        [PHASE.RIGHT_REAR_PREPARATION_2]: async () => {
          await this.executeRearBleeding(); // 右後輪 実行(2回目)へ遷移
        },
        [PHASE.LEFT_REAR_EXECUTE_BLEEDING_1]: async () => {
          await this.stopRearBleeding(); // 左後輪 停止(1回目)へ遷移
        },
        [PHASE.LEFT_REAR_PREPARATION_2]: async () => {
          await this.executeRearBleeding(); // 左後輪 実行(2回目)へ遷移
        },
        [PHASE.EXECUTE_CONFIRMATION]: async () => {
          await this.executeBleedingElectric(); // エアー抜きシーケンス1を実行
        },
        [PHASE.START_CONFIRMATION]: async () => {
          this.phase = PHASE.AIR_BLEEDING_7;
          await this.executeBleedingCheck(); // エアー抜きシーケンス(7回目)を実行
        },
        [PHASE.AIR_BLEEDING_COMPLETED]: () => {
          this.closeWizard();
          return Promise.resolve();
        },
        [PHASE.ERROR]: () => {
          this.closeWizard();
          return Promise.resolve();
        },
        [PHASE.FAILED]: async () => {
          await this.failedProcess();
        }
      };
      const handler = handlers[this.phase];
      if (handler) {
        await handler();
      }
    },

    /** キャンセルボタンクリック時の処理 */
    async onCancelClicked() {
      const handlers = {
        [PHASE.WAIT]: () => {
          this.closeWizard();
        },
        [PHASE.START]: () => {
          this.closeWizard();
        },
      };
      const handler = handlers[this.phase];
      if (handler) {
        handler();
      }
    },

    /** YESボタンクリック時の処理 */
    async onYesClicked() {
      // タイマー停止
      clearInterval(this.timer);

      await executeApi(this, 'stop-air_bleed_rear_second',
        // status: completed 時処理
        async (self) => {
          if (self.phase === PHASE.RIGHT_REAR_EXECUTE_BLEEDING_2) {
            // 右後輪処理中：実行状態: 準備(左後輪)へ遷移
            self.phase = PHASE.LEFT_REAR_PREPARATION_1;
            await this.executePreparation();
          } else {
            // 左後輪処理中：実行確認へ遷移
            self.phase = PHASE.EXECUTE_CONFIRMATION;
          }
        },
        // status: failed 時処理
        async (self, response) => { await handleResponseFailed(self, response); }
      );
    },

    /**
     * 完了フラグ初期化の処理
     */
    async writeInitialFlag() {
      await executeApi(this, 'write_session_transition',
        // status: completed 時処理
        (self) => { self.phase = PHASE.RIGHT_FRONT_CAUTION; }, // 注意表示(右前輪)へ遷移
        // status: failed 時処理
        async (self, response) => { await handleResponseFailed(self, response); }
      );
    },

    /**
     * 右前輪/左前輪 エアー抜き(N/O・N/C弁強制駆動)の処理
     */
    async executeDrivingNoNc() {
      await executeApi(this, 'execute_with_wait_time-NoNc_exec',
        // status: completed 時処理
        async (self) => {
          await util.sleep(1000);
          if (self.executeCount === 0) {
            // 実行1回目
            self.executeCount = 1;
            // 再度エアー抜き(N/O・N/C弁強制駆動)を実行
            await self.executeDrivingNoNc();
          } else {
            // 実行２回目
            // カウントをリセット
            self.executeCount = 0;
            // エアー抜き(ポンプモータ強制駆動)を実行
            await self.executeDrivingPump();
          }
        },
        // status: failed 時処理
        async (self, response) => { await handleResponseFailed(self, response); }
      );
    },

    /**
     * 右前輪/左前輪 エアー抜き(ポンプモータ強制駆動)の処理
     */
    async executeDrivingPump() {
      await executeApi(this, 'execute_with_wait_time-pomp_exec',
        // status: completed 時処理
        (self) => {
          if (self.phase === PHASE.RIGHT_FRONT_NO_NC_VALVE) {
            // 右前輪処理中：エアー抜き(ブリーダーバルブ開閉操作指示)(右前輪)へ遷移
            self.phase = PHASE.RIGHT_FRONT_BLEEDER_VALVE;
          } else {
            // 左前輪処理中：エアー抜き(ブリーダーバルブ開閉操作指示)(左前輪)へ遷移
            self.phase = PHASE.LEFT_FRONT_BLEEDER_VALVE;
          }
        },
        // status: failed 時処理
        async (self, response) => { await handleResponseFailed(self, response); }
      );
    },

    /**
     * 右前輪/左前輪 エアー抜き(エアー抜き実施)の処理
     */
    async executeBleeding() {
      await executeApi(this, 'execute-air_bleed_front',
        // status: completed 時処理
        (self) => {
          if (self.phase === PHASE.RIGHT_FRONT_BLEEDER_VALVE_CONFIRMED) {
            // 右前輪処理中：エアー抜き(エアー抜き実施)(右前輪)の文言を表示
            self.phase = PHASE.RIGHT_FRONT_EXECUTE_BLEEDING;
          } else {
            // 左前輪処理中：エアー抜き(エアー抜き実施)(左前輪)の文言を表示
            self.phase = PHASE.LEFT_FRONT_EXECUTE_BLEEDING;
          }
        },
        // status: failed 時処理
        async (self, response) => { await handleResponseFailed(self, response); }
      );
    },

    /**
     * 右前輪/左前輪 エアー抜き(エアー抜き停止)の処理
     */
    async stopBleeding() {
      await executeApi(this, 'stop-air_bleed_front',
        // status: completed 時処理
        (self) => {
          if (self.phase === PHASE.RIGHT_FRONT_EXECUTE_BLEEDING) {
            // 右前輪処理中：注意表示(左前輪)へ遷移
            self.phase = PHASE.LEFT_FRONT_CAUTION;
          } else {
            // 左前輪処理中：開始確認(右後輪)へ遷移
            self.phase = PHASE.RIGHT_REAR_START;
          }
        },
        // status: failed 時処理
        async (self, response) => { await handleResponseFailed(self, response); }
      );
    },

    /**
     * 右後輪/左後輪 準備の処理
     */
    async executePreparation() {
      // 左後輪処理の場合は指定時間待機する
      if (this.phase === PHASE.LEFT_REAR_PREPARATION_1) await util.sleep(1000);

      await executeApi(this, 'execute-preparation_rear',
        // 結果に関わらず次処理を実行する
        // status: completed 時処理
        async (self) => {
          await util.sleep(1000);
          await self.executePrepararingNoNc(); // 右後輪/左後輪 準備中(N/O・N/C弁強制駆動)へ遷移
        },
        // status: failed 時処理
        async (self) => {
          await util.sleep(1000);
          await self.executePrepararingNoNc(); // 右後輪/左後輪 準備中(N/O・N/C弁強制駆動)へ遷移
        },
      );
    },

    /**
     * 右後輪/左後輪 準備中(N/O・N/C弁強制駆動)の処理
     */
    async executePrepararingNoNc() {
      await executeApi(this, 'execute_with_wait_time-NoNc_preparation',
        // status: completed 時処理
        async (self) => {
          await util.sleep(1000);
          if (self.executeCount === 0) {
            // 実行1回目
            self.executeCount = 1;
            // 再度 右後輪/左後輪 準備中(N/O・N/C弁強制駆動)を実行
            await self.executePrepararingNoNc();
          } else {
            // 実行２回目
            // カウントをリセット
            self.executeCount = 0;
            // 右後輪/左後輪 準備中(ポンプモータ強制駆動)を実行
            await self.executePreparingPump();
          }
        },
        // status: failed 時処理
        async (self, response) => { await handleResponseFailed(self, response); }
      );
    },

    /**
     * 右後輪/左後輪 準備中(ポンプモータ強制駆動)の処理
     */
    async executePreparingPump() {
      await executeApi(this, 'execute_with_wait_time-pomp_preparation',
        // status: completed 時処理
        (self) => {
          if (self.phase === PHASE.RIGHT_REAR_PREPARATION_1) {
            // 右後輪処理中：実行(エアー抜き)(右後輪)へ遷移
            self.phase = PHASE.RIGHT_REAR_EXECUTE_BLEEDING_1;
          } else {
            // 左後輪処理中：実行(エアー抜き)(左後輪)へ遷移
            self.phase = PHASE.LEFT_REAR_EXECUTE_BLEEDING_1;
          }
        },
        // status: failed 時処理
        async (self, response) => { await handleResponseFailed(self, response); }
      );
    },

    /**
     * 右後輪/左後輪 停止(1回目)の処理
     */
    async stopRearBleeding() {
      await executeApi(this, 'stop-air_bleed_rear',
        // status: completed 時処理
        (self) => {
          if (self.phase === PHASE.RIGHT_REAR_EXECUTE_BLEEDING_1) {
            // 右後輪処理中：エアー抜き2回目の準備(右後輪)へ遷移
            self.phase = PHASE.RIGHT_REAR_PREPARATION_2;
          } else {
            // 左後輪処理中：エアー抜き2回目の準備(左後輪)へ遷移
            self.phase = PHASE.LEFT_REAR_PREPARATION_2;
          }
        },
        // status: failed 時処理
        async (self, response) => { await handleResponseFailed(self, response); }
      );
    },

    /**
     * 右後輪/左後輪 実行(2回目)の処理
     */
    async executeRearBleeding() {
      await executeApi(this, 'execute-air_bleed_rear',
        // status: completed 時処理
        (self) => {
          // 右後輪/左後輪 準備(2回目)から遷移の場合は実行(2回目)へ遷移
          if (self.phase === PHASE.RIGHT_REAR_PREPARATION_2) {
            self.phase = PHASE.RIGHT_REAR_EXECUTE_BLEEDING_2;
          } else if (self.phase === PHASE.LEFT_REAR_PREPARATION_2) {
            self.phase = PHASE.LEFT_REAR_EXECUTE_BLEEDING_2;
          }
          // カウントダウン開始
          this.startCountdown();
        },
        // status: failed 時処理
        async (self, response) => { await handleResponseFailed(self, response); }
      );
    },

    /**
     * エアー抜きシーケンス1の処理
     */
    async executeBleedingElectric() {
      await executeApi(this, 'execute-air_bleed_electric',
        // status: completed 時処理
        async (self) => {
          // 指定時間(2秒間)経過後、エアー抜きシーケンス2に遷移
          await util.sleep(2000);
          this.bleedingCount++;
          self.phase = PHASE.AIR_BLEEDING_SEQ_2;
          await self.getBleedingElectriResult();
        },
        // status: failed 時処理
        async (self, response) => { await handleResponseFailed(self, response); }
      );
    },

    /**
     * エアー抜きシーケンス2の処理
     */
    async getBleedingElectriResult() {
      await executeApi(this, 'read-electric',
        // status: completed 時処理
        async (self, response) => {
          await handleResponseResult(self, response,
            // result: ok 時処理
            async (self) => {
              // 指定時間(6秒間)経過後、エアー抜きシーケンス3を実行
              await util.sleep(6000);
              await self.getBleedingbrakeResult();
            },
            // result: retry 時処理
            async (self) => { await self.getBleedingElectriResult(); } // 再度エアー抜きシーケンス2を実行
          );
        },
        // status: failed 時処理
        async (self, response) => { await handleResponseFailed(self, response); }
      );
    },

    /**
     * エアー抜きシーケンス3の処理
     */
    async getBleedingbrakeResult() {
      this.phase = PHASE.AIR_BLEEDING_SEQ_3;
      await executeApi(this, 'read-brake',
        // status: completed 時処理
        async (self, response) => {
          await handleResponseResult(self, response,
            // result: ok 時処理
            async (self) => {
              if (self.bleedingCount === 6) {
                // 実行回数が6回目の場合、エアー抜き(7回目)開始確認へ遷移
                self.phase = PHASE.START_CONFIRMATION;
              } else {
                // 実行回数が6回目未満の場合、エアー抜きシーケンス1を実行
                await self.executeBleedingElectric();
              }
            },
            // result: retry 時処理
            async (self) => { await self.getBleedingbrakeResult(); } // 再度エアー抜きシーケンス3を実行
          );
        },
        // status: failed 時処理
        async (self, response) => { await handleResponseFailed(self, response); }
      );
    },

    /**
     * エアー抜きシーケンス(7回目)の処理
     */
    async executeBleedingCheck() {
      await executeApi(this, 'execute_check',
        // status: completed 時処理
        async (self) => {
          // 完了フラグ設定へ遷移
          await self.writeComletedFlag();
        },
        // status: failed 時処理
        async (self, response) => { await handleResponseFailed(self, response); }
      );
    },

    /**
     * 完了フラグ設定の処理
     */
    async writeComletedFlag() {
      await executeApi(this, 'write',
        // status: completed 時処理
        async (self) => {
          // 終了処理を実行
          await self.exitProcess();
        },
        // status: failed 時処理
        async (self, response) => { await handleResponseFailed(self, response); }
      );
    },

    /**
     * 終了処理
     */
    async exitProcess() {
      await executeApi(this, 'exit',
        // 結果に関わらずエアー抜き完了表示へ遷移
        (self) => { self.phase = PHASE.AIR_BLEEDING_COMPLETED; },
        (self) => { self.phase = PHASE.AIR_BLEEDING_COMPLETED; }
      );
    },

    /**
     * 共通エラー時の処理
     */
    async failedProcess() {
      await executeApi(this, this.isDispose ? 'dispose' : 'exit',
        // 結果に関わらず作業サポート一覧へ遷移
        (self) => { self.closeWizard(); },
        (self) => { self.closeWizard(); }
      );
    },

    /**
     * 実行(2回目)画面でのカウントダウン開始処理
     */
    startCountdown() {
      // 初期化
      this.countdown = 120;
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }

      this.timer = setInterval(() => {
        if (this.countdown > 0) {
          this.countdown--;
        } else {
          clearInterval(this.timer);
        }
      }, 1000);
    },

    /** その他エラー発生時の表示 */
    showUnexpectedError() {
      // 異常終了：エラー表示
      this.phase = PHASE.FAILED;
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     */
    validateErrorResult(result) {
      apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.showUnexpectedError(); },
      );
    },
  },

  async mounted() {
    this.init();
  },
};
